import { Box, Button, CircularProgress, Link, Skeleton, Stack, Typography } from '@mui/material';
import type { ChangeEvent, RefObject } from 'react';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';

/**
 * The uploaded source-file bar shared by the Draft review and the Ready detail
 * view: the file icon, filename, and a "Source file" caption. The owner-only
 * "Change file" affordance (a plain-text link that opens the OS picker directly
 * — no in-view step, no confirm modal) is Draft-only; pass ``canEdit`` plus the
 * file-replace handlers to enable it. The Ready view renders the same bar
 * read-only (no switching, even for the owner) by omitting them.
 *
 * ``gutter`` is the horizontal inset (spacing units): the Draft body sits at 3,
 * the Ready view at 6 so the bar lines up with the shared header + concepts.
 */
export const ConceptGroupSourceFileBar = ({
  sourceFilename,
  testIdPrefix = 'cg-preview',
  gutter = 3,
  canEdit = false,
  /** First-load of the owning detail — skeleton the filename instead of a fallback. */
  isLoading = false,
  isReplacing = false,
  fileError = null,
  fileInputRef,
  onFilePicked,
  onOpenPicker,
  /** Keys the hidden input so picking the same file after a replace re-fires. */
  inputKey,
  /** When set, the filename becomes a link that triggers this download. */
  onDownload,
}: {
  sourceFilename: string;
  testIdPrefix?: string;
  gutter?: number;
  /** Draft-only: the owner may replace the source file. Omit for a read-only bar. */
  canEdit?: boolean;
  isLoading?: boolean;
  isReplacing?: boolean;
  fileError?: string | null;
  fileInputRef?: RefObject<HTMLInputElement>;
  onFilePicked?: (file: File | null) => void;
  onOpenPicker?: () => void;
  inputKey?: string;
  onDownload?: () => void;
}) => {
  const { formatMessage } = useIntl();
  const canReplace = !isLoading && canEdit && Boolean(onOpenPicker && onFilePicked);
  return (
    <>
      <Stack
        direction="row"
        data-testid={`${testIdPrefix}-source-file-bar`}
        sx={{
          alignItems: 'center',
          gap: 2,
          marginX: gutter,
          // The header's meta row already sits 1.5 above the bar; +0.5 here makes
          // the top gap total 2, matching marginBottom for symmetric spacing.
          marginTop: 0.5,
          marginBottom: 2,
          paddingX: 4,
          paddingY: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          bgcolor: 'grey.50',
        }}
      >
        <Icon
          id={`${testIdPrefix}-source-file-icon`}
          icon="file"
          width={24}
          height={24}
          sx={{ flexShrink: 0 }}
        />
        {isLoading ? (
          <Skeleton
            variant="text"
            width={200}
            data-testid={`${testIdPrefix}-source-file-loading`}
          />
        ) : onDownload ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onDownload}
            data-testid={`${testIdPrefix}-source-file-download`}
            sx={{
              fontWeight: 500,
              minWidth: 0,
              wordBreak: 'break-word',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            {sourceFilename}
          </Link>
        ) : (
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ minWidth: 0, wordBreak: 'break-word' }}
          >
            {sourceFilename}
          </Typography>
        )}
        {/* Change file is owner-only and Draft-only (OR-6288 permissions); the
            Ready view + non-owners get a read-only source-file bar. */}
        {canReplace && (
          <>
            {/* Hidden input keyed by previewId so picking the same file after a
                replace still fires onChange on the fresh draft. */}
            <Box
              key={inputKey}
              component="input"
              type="file"
              accept=".csv,.xlsx,.xls"
              ref={fileInputRef}
              data-testid={`${testIdPrefix}-file-input`}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onFilePicked?.(event.target.files?.[0] ?? null);
                event.target.value = '';
              }}
              sx={{ display: 'none' }}
            />
            {/* Figma: a plain text link, no border/icon (design feedback). */}
            <Button
              variant="text"
              size="small"
              startIcon={isReplacing ? <CircularProgress size={16} color="inherit" /> : undefined}
              onClick={onOpenPicker}
              disabled={isReplacing}
              sx={{ marginLeft: 'auto', textTransform: 'none', flexShrink: 0 }}
              data-testid={`${testIdPrefix}-change-file`}
            >
              {formatMessage({ id: 'epiChat.conceptGroupPreview.sourceFile.change' })}
            </Button>
          </>
        )}
      </Stack>
      {fileError ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ paddingX: gutter, paddingBottom: 1 }}
          data-testid={`${testIdPrefix}-file-error`}
        >
          {fileError}
        </Typography>
      ) : null}
    </>
  );
};
